/**
 * ReportAbuseModal - Modal component for reporting abusive content
 */

import { useState } from 'react';
import { X, Flag, Loader2, AlertTriangle, CheckCircle } from 'lucide-react';
import { createReport, getReportReasonsForLanguage } from '../../services/reportsService';
import { useLanguage } from '../../context/LanguageContext';
import { getStoredUser as getUser } from '../../context/AuthContext';

const ReportAbuseModal = ({
    isOpen,
    onClose,
    contentType, // 'advertisement' or 'website'
    contentId,
    contentUuid,
    contentTitle
}) => {
    const { language } = useLanguage();
    const user = getUser();

    const [reason, setReason] = useState('');
    const [description, setDescription] = useState('');
    const [reporterEmail, setReporterEmail] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState(null);

    const reasons = getReportReasonsForLanguage(language);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        if (!reason) {
            setError(language === 'fr'
                ? 'Veuillez sélectionner une raison'
                : 'Please select a reason');
            return;
        }

        // Require email for non-logged users
        if (!user && !reporterEmail) {
            setError(language === 'fr'
                ? 'Veuillez entrer votre adresse email'
                : 'Please enter your email address');
            return;
        }

        setSubmitting(true);

        try {
            const result = await createReport({
                contentType,
                contentId,
                contentUuid,
                reason,
                description,
                reporterEmail: !user ? reporterEmail : undefined
            });

            if (result.success) {
                setSubmitted(true);
            } else {
                setError(result.message);
            }
        } catch (err) {
            setError(language === 'fr'
                ? 'Une erreur est survenue. Veuillez réessayer.'
                : 'An error occurred. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleClose = () => {
        setReason('');
        setDescription('');
        setReporterEmail('');
        setError(null);
        setSubmitted(false);
        onClose();
    };

    if (!isOpen) return null;

    const contentTypeLabel = contentType === 'advertisement'
        ? (language === 'fr' ? 'cette publicité' : 'this advertisement')
        : (language === 'fr' ? 'ce site web' : 'this website');

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/50"
                onClick={handleClose}
            />

            {/* Modal */}
            <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                            <Flag className="w-5 h-5 text-red-600 dark:text-red-400" />
                        </div>
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                            {language === 'fr' ? 'Signaler un abus' : 'Report abuse'}
                        </h2>
                    </div>
                    <button
                        onClick={handleClose}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                    >
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-4 overflow-y-auto max-h-[calc(90vh-140px)]">
                    {submitted ? (
                        /* Success message */
                        <div className="text-center py-8">
                            <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                                <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                                {language === 'fr' ? 'Signalement envoyé' : 'Report submitted'}
                            </h3>
                            <p className="text-gray-600 dark:text-gray-400 mb-6">
                                {language === 'fr'
                                    ? 'Merci pour votre signalement. Notre équipe examinera ce contenu dans les plus brefs délais.'
                                    : 'Thank you for your report. Our team will review this content as soon as possible.'}
                            </p>
                            <button
                                onClick={handleClose}
                                className="px-6 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                            >
                                {language === 'fr' ? 'Fermer' : 'Close'}
                            </button>
                        </div>
                    ) : (
                        /* Report form */
                        <form onSubmit={handleSubmit} className="space-y-4">
                            {/* Info about what is being reported */}
                            <div className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                                <div className="text-sm text-gray-600 dark:text-gray-400">
                                    {language === 'fr'
                                        ? `Vous êtes sur le point de signaler ${contentTypeLabel}`
                                        : `You are about to report ${contentTypeLabel}`}
                                    {contentTitle && (
                                        <span className="font-medium text-gray-900 dark:text-white">
                                            {' '}"{contentTitle}"
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Reason selection */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    {language === 'fr' ? 'Raison du signalement' : 'Reason for report'} *
                                </label>
                                <select
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                    required
                                >
                                    <option value="">
                                        {language === 'fr' ? '-- Sélectionnez --' : '-- Select --'}
                                    </option>
                                    {reasons.map(r => (
                                        <option key={r.value} value={r.value}>
                                            {r.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Description */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    {language === 'fr' ? 'Description (optionnel)' : 'Description (optional)'}
                                </label>
                                <textarea
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    rows={3}
                                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                                    placeholder={language === 'fr'
                                        ? 'Décrivez pourquoi ce contenu devrait être retiré...'
                                        : 'Describe why this content should be removed...'}
                                />
                            </div>

                            {/* Email for non-logged users */}
                            {!user && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        {language === 'fr' ? 'Votre email' : 'Your email'} *
                                    </label>
                                    <input
                                        type="email"
                                        value={reporterEmail}
                                        onChange={(e) => setReporterEmail(e.target.value)}
                                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                        placeholder="email@example.com"
                                        required
                                    />
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                        {language === 'fr'
                                            ? 'Nous pourrions vous contacter pour plus d\'informations.'
                                            : 'We may contact you for more information.'}
                                    </p>
                                </div>
                            )}

                            {/* Error message */}
                            {error && (
                                <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
                                    {error}
                                </div>
                            )}

                            {/* Submit button */}
                            <div className="flex gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={handleClose}
                                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                >
                                    {language === 'fr' ? 'Annuler' : 'Cancel'}
                                </button>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                >
                                    {submitting ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            {language === 'fr' ? 'Envoi...' : 'Sending...'}
                                        </>
                                    ) : (
                                        <>
                                            <Flag className="w-4 h-4" />
                                            {language === 'fr' ? 'Signaler' : 'Report'}
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ReportAbuseModal;
